import { z } from 'zod'
import {
  paymentStatusSchema,
  salesPaymentKindSchema,
  salesRecordMethodSchema,
  salesRecordStatusSchema,
  salesRefundKindSchema,
  salesRefundStatusSchema,
} from '../enums/sales-payment.enum.js'
import { calendarDaySchema, instantSchema } from '../validators/temporal.validator.js'


/**
 * `/pay/` and a link token, and nothing else. The app copies it after its own
 * origin and links to it, so `//host`, `/\host`, a scheme or an `@` would take
 * whoever follows it off this site. The token is base64url, which none of those
 * can be spelled in.
 */
const payPathSchema = z.string().regex(/^\/pay\/[A-Za-z0-9_-]+$/, 'Not a payment link')

/** Payments (docs/sales.spec.md, "Paying"). Money is integer cents. */

/** Money given back from a record, with the reason the payer was told. */
export const salesRefundDtoSchema = z.object({
  id: z.string().uuid(),
  kind: salesRefundKindSchema,
  status: salesRefundStatusSchema,
  amountCents: z.number().int(),
  reason: z.string(),
  createdAt: instantSchema,
})

export const paymentRecordDtoSchema = z.object({
  id: z.string().uuid(),
  method: salesRecordMethodSchema,
  status: salesRecordStatusSchema,
  amountCents: z.number().int(),
  discountCents: z.number().int(),
  payerName: z.string(),
  reference: z.string(),
  receivedAt: instantSchema.nullable(),
  refunds: z.array(salesRefundDtoSchema).default([]),
  /** What can still be given back from it: what was charged or received, less refunds so far. */
  refundableCents: z.number().int().default(0),
})
export type PaymentRecordDto = z.infer<typeof paymentRecordDtoSchema>

/** A security deposit settled after the event: what went back (none, if kept) and why. */
export const depositReturnDtoSchema = z.object({
  returnedCents: z.number().int(),
  reason: z.string(),
  at: instantSchema,
})

/** One payment as staff see it: with the money toward it and the link to pay it. */
export const salesPaymentDtoSchema = z.object({
  id: z.string().uuid(),
  packetId: z.string().uuid(),
  kind: salesPaymentKindSchema,
  label: z.string(),
  amountCents: z.number().int(),
  /** Null: due on signing. */
  dueOn: calendarDaySchema.nullable(),
  status: paymentStatusSchema,
  /** What counts toward paying it. Refunds don't take from it: a refunded payment isn't owed again. */
  creditedCents: z.number().int(),
  /** What's been given back from it. */
  refundedCents: z.number().int().default(0),
  /** For a security deposit, once it's settled. */
  depositReturn: depositReturnDtoSchema.nullable().default(null),
  /** The path anyone with it can pay through: `/pay/<token>`. */
  payPath: payPathSchema,
  records: z.array(paymentRecordDtoSchema),
})
export type SalesPaymentDto = z.infer<typeof salesPaymentDtoSchema>

/**
 * A booking's revenue (docs/sales.spec.md, "Revenue"): what's agreed under the
 * agreement and its invoices, and what's been collected toward it, less
 * refunds. Tax and security deposits are shown apart.
 */
export const salesRevenueDtoSchema = z.object({
  /** The current agreement's total and every invoice's, tax included. */
  agreedCents: z.number().int(),
  /** The tax in what's agreed. */
  taxCents: z.number().int(),
  /** Paid toward what's agreed, less what went back. */
  collectedCents: z.number().int(),
  refundedCents: z.number().int(),
  /** Owed and not yet paid. */
  stillDueCents: z.number().int(),
  /** Security deposits paid and not given back: held, not revenue. */
  depositsHeldCents: z.number().int(),
})
export type SalesRevenueDto = z.infer<typeof salesRevenueDtoSchema>

export const EMPTY_SALES_REVENUE: SalesRevenueDto = { agreedCents: 0, taxCents: 0, collectedCents: 0, refundedCents: 0, stillDueCents: 0, depositsHeldCents: 0 }

/** The couple's saved method, while autopay is on: "Visa ending 4242". */
export const autopayDtoSchema = z.object({ label: z.string() })
export type AutopayDto = z.infer<typeof autopayDtoSchema>

/** One payment as the couple and their planner see it; the link only for the couple. */
export const bookingPaymentDtoSchema = z.object({
  id: z.string().uuid(),
  kind: salesPaymentKindSchema,
  label: z.string(),
  amountCents: z.number().int(),
  dueOn: calendarDaySchema.nullable(),
  status: paymentStatusSchema,
  creditedCents: z.number().int(),
  refundedCents: z.number().int().default(0),
  depositReturn: depositReturnDtoSchema.nullable().default(null),
  /** Null for a planner, who sees what's owed but doesn't pay it. */
  payPath: payPathSchema.nullable(),
})
export type BookingPaymentDto = z.infer<typeof bookingPaymentDtoSchema>

/**
 * A payment as its link shows it, to whoever has the link (docs/sales.spec.md,
 * "Paying"): a forwarded link reveals nobody's name or contact details.
 */
export const publicPaymentDtoSchema = z.object({
  venueName: z.string(),
  label: z.string(),
  kind: salesPaymentKindSchema,
  /** What's left to pay by card. */
  amountCents: z.number().int(),
  /** What's left to pay by bank transfer, after the venue's discount; null when it offers none. */
  bankChargeCents: z.number().int().nullable(),
  bankDiscountCents: z.number().int(),
  dueOn: calendarDaySchema.nullable(),
  status: paymentStatusSchema,
  /** When it was paid in full, for a paid one. */
  paidAt: instantSchema.nullable(),
  eventDate: calendarDaySchema,
  /**
   * The couple, signed in, can save the method to pay later payments
   * automatically: with the deposit, or with a later payment once autopay
   * was turned off.
   */
  canSaveForLater: z.boolean().default(false),
  /** For Stripe's form in the browser: the business paid, and the platform's key. */
  businessAccountId: z.string(),
  publishableKey: z.string(),
})
export type PublicPaymentDto = z.infer<typeof publicPaymentDtoSchema>

/** What paying starts: the secret Stripe's form confirms with, and the amount it will charge. */
export const paymentIntentDtoSchema = z.object({
  clientSecret: z.string(),
  chargeCents: z.number().int(),
})
export type PaymentIntentDto = z.infer<typeof paymentIntentDtoSchema>
